import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  CircularProgress,
  Divider,
  Chip,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AutoAwesomeIcon from "@material-ui/icons/Stars";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import ChatiaDialog from "../ChatiaDialog";
import { SaveButton } from "../ActionButtons";

const useStyles = makeStyles((theme) => ({
  summaryCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
  },
  summaryHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  summaryDate: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  summaryText: {
    fontSize: "0.85rem",
    whiteSpace: "pre-wrap",
    lineHeight: 1.6,
    color: theme.palette.text.primary,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  chipManual: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    height: 22,
    fontSize: "0.7rem",
    borderRadius: 50,
  },
  chipAuto: {
    backgroundColor: "#43a047",
    color: "#fff",
    height: 22,
    fontSize: "0.7rem",
    borderRadius: 50,
  },
  msgCount: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "12px 16px",
  },
}));

const AiSummaryModal = ({ open, onClose, ticketId }) => {
  const classes = useStyles();
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadSummaries = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/tickets/${ticketId}/ai-summaries`);
      setSummaries(data);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [ticketId]);

  useEffect(() => {
    if (open) {
      loadSummaries();
    }
  }, [open, loadSummaries]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await api.post(`/tickets/${ticketId}/ai-summaries`);
      toast.success("Resumo IA gerado com sucesso!");
      await loadSummaries();
    } catch (err) {
      toastError(err);
    }
    setGenerating(false);
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  return (
    <ChatiaDialog
      open={open}
      onClose={onClose}
      title="Resumos IA"
      maxWidth="sm"
      onSave={handleGenerate}
      saveText={generating ? "Gerando..." : "Gerar Novo Resumo"}
      saveLoading={generating}
      saveDisabled={generating}
      saveType="button"
    >
      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={32} />
        </div>
      ) : summaries.length === 0 ? (
        <div className={classes.emptyState}>
          <AutoAwesomeIcon style={{ fontSize: 48, opacity: 0.3, marginBottom: 8 }} />
          <Typography variant="body2">
            Nenhum resumo gerado ainda.
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Clique em "Gerar Novo Resumo" para analisar a conversa.
          </Typography>
        </div>
      ) : (
        summaries.map((s) => (
          <Paper key={s.id} className={classes.summaryCard} elevation={0}>
            <div className={classes.summaryHeader}>
              <span className={classes.summaryDate}>
                {formatDate(s.createdAt)}
              </span>
              <Chip
                label={s.generatedBy === "automatic" ? "Automático" : "Manual"}
                className={s.generatedBy === "automatic" ? classes.chipAuto : classes.chipManual}
                size="small"
              />
            </div>
            <div className={classes.msgCount}>
              {s.messageCount} mensagens analisadas
              {s.user && ` • por ${s.user.name}`}
            </div>
            <Divider style={{ margin: "8px 0" }} />
            <div className={classes.summaryText}>
              {s.summaryText}
            </div>
          </Paper>
        ))
      )}
    </ChatiaDialog>
  );
};

export default AiSummaryModal;
